import { TrainFront } from 'lucide-react';
import { RAIL_KINDS, railOf, type RailContent, type RailLine, type RailStop } from '@london-life/contracts';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { Textarea } from '@london-life/ui/components/textarea';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, number, toPence, within } from './form';

const KIND_LABELS = { metro: 'Tube & DLR', rail: 'National Rail' } as const;

// King's Cross (as Lagos Life's Jibowu Motor Park): the lines and what each is like, the stops on the map the
// trains run to with their fares and times, the cities up the line that open soon, the travel insurance and what
// the trip's card says. Part of the content draft, published with it.
export function TrainsPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const rail = railOf(working ?? {});
  const change = (fn: (draft: RailContent) => void) =>
    editor.edit((content) => {
      content.rail ??= structuredClone(railOf(content));
      fn(content.rail);
    });
  const line = (id: string, fn: (item: RailLine) => void) =>
    change((draft) => {
      const item = draft.lines.find((entry) => entry.id === id);
      if (item) fn(item);
    });
  const stop = (placeId: string, fn: (item: RailStop) => void) =>
    change((draft) => {
      const item = draft.stops.find((entry) => entry.placeId === placeId);
      if (item) fn(item);
    });
  return (
    <section>
      <PageHeader
        title="Trains"
        description="King's Cross's ticket office (as Lagos Life's motor park): the lines, the stops they run to on the map and their fares, the cities that open soon, the travel insurance and the trip's card. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The station">
            <h2 className="content-subtitle">
              <TrainFront size={16} aria-hidden="true" /> The station
            </h2>
            <div className="content-grid">
              <Field id="rail-name" label="Name">
                <Input
                  id="rail-name"
                  value={rail.name}
                  maxLength={40}
                  onChange={(e) => change((draft) => void (draft.name = e.target.value))}
                />
              </Field>
              <Field id="rail-platforms" label="Platforms">
                <Input
                  id="rail-platforms"
                  type="number"
                  min={1}
                  max={30}
                  value={rail.platforms}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.platforms = within(Math.round(number(e.target.value, rail.platforms)), 1, 30)),
                    )
                  }
                />
              </Field>
              <Field id="rail-boarding" label="On the platform (seconds)">
                <Input
                  id="rail-boarding"
                  type="number"
                  min={0}
                  max={120}
                  value={rail.boardingSeconds}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.boardingSeconds = within(
                          Math.round(number(e.target.value, rail.boardingSeconds)),
                          0,
                          120,
                        )),
                    )
                  }
                />
              </Field>
              <Field id="rail-cover" label="Travel insurance (£)">
                <Input
                  id="rail-cover"
                  type="number"
                  min={0}
                  step="0.01"
                  value={rail.insurance.pence / 100}
                  onChange={(e) =>
                    change((draft) => void (draft.insurance.pence = toPence(e.target.value, rail.insurance.pence)))
                  }
                />
              </Field>
              <Field id="rail-loss" label="A bag goes missing (% of trips)">
                <Input
                  id="rail-loss"
                  type="number"
                  min={0}
                  max={50}
                  value={rail.insurance.lossPercent}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.insurance.lossPercent = within(
                          Math.round(number(e.target.value, rail.insurance.lossPercent)),
                          0,
                          50,
                        )),
                    )
                  }
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Lines">
            <h2 className="content-subtitle">Lines</h2>
            <p className="admin-muted">
              Each line runs to the stops ticked for it. Its fare is the stop's fare times its own; First Class (where a
              line has it) costs more again.
            </p>
            {rail.lines.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`line-${item.id}`}>
                <Field id={`line-${item.id}-name`} label="Name">
                  <Input
                    id={`line-${item.id}-name`}
                    value={item.name}
                    maxLength={30}
                    onChange={(e) => line(item.id, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`line-${item.id}-kind`} label="Kind">
                  <select
                    id={`line-${item.id}-kind`}
                    className="content-select"
                    value={item.kind}
                    onChange={(e) => line(item.id, (entry) => void (entry.kind = e.target.value as RailLine['kind']))}
                  >
                    {RAIL_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {KIND_LABELS[kind]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id={`line-${item.id}-times`} label="Times the stop's fare">
                  <Input
                    id={`line-${item.id}-times`}
                    type="number"
                    min={0.5}
                    max={5}
                    step="0.05"
                    value={item.times}
                    onChange={(e) =>
                      line(item.id, (entry) => void (entry.times = within(number(e.target.value, item.times), 0.5, 5)))
                    }
                  />
                </Field>
                <Field id={`line-${item.id}-comfort`} label="Comfort (1–3 stars)">
                  <Input
                    id={`line-${item.id}-comfort`}
                    type="number"
                    min={1}
                    max={3}
                    value={item.comfort}
                    onChange={(e) =>
                      line(
                        item.id,
                        (entry) =>
                          void (entry.comfort = within(Math.round(number(e.target.value, item.comfort)), 1, 3)),
                      )
                    }
                  />
                </Field>
                <Field id={`line-${item.id}-rating`} label="Rating (1–5)">
                  <Input
                    id={`line-${item.id}-rating`}
                    type="number"
                    min={1}
                    max={5}
                    step="0.1"
                    value={item.rating}
                    onChange={(e) =>
                      line(item.id, (entry) => void (entry.rating = within(number(e.target.value, item.rating), 1, 5)))
                    }
                  />
                </Field>
                <Field id={`line-${item.id}-perks`} label="On board">
                  <Input
                    id={`line-${item.id}-perks`}
                    value={item.perks}
                    maxLength={80}
                    onChange={(e) => line(item.id, (entry) => void (entry.perks = e.target.value))}
                  />
                </Field>
                {(['body', 'band', 'doors'] as const).map((part) => (
                  <Field key={part} id={`line-${item.id}-${part}`} label={`Paint: ${part}`}>
                    <Input
                      id={`line-${item.id}-${part}`}
                      type="color"
                      value={item.colours[part]}
                      onChange={(e) => line(item.id, (entry) => void (entry.colours[part] = e.target.value))}
                    />
                  </Field>
                ))}
                {item.first && (
                  <Field id={`line-${item.id}-first`} label="First Class: times Standard">
                    <Input
                      id={`line-${item.id}-first`}
                      type="number"
                      min={1}
                      max={10}
                      step="0.05"
                      value={item.first.times}
                      onChange={(e) =>
                        line(
                          item.id,
                          (entry) =>
                            void (entry.first = {
                              ...entry.first!,
                              times: within(number(e.target.value, entry.first!.times), 1, 10),
                            }),
                        )
                      }
                    />
                  </Field>
                )}
                <div className="admin-field">
                  <span className="admin-muted">Runs to</span>
                  {rail.stops.map((target) => (
                    <label className="designer-check" key={target.placeId}>
                      <Switch
                        checked={item.serves.includes(target.placeId)}
                        aria-label={`${item.name} runs to ${target.name}`}
                        onCheckedChange={(on) =>
                          line(
                            item.id,
                            (entry) =>
                              void (entry.serves = on
                                ? [...new Set([...entry.serves, target.placeId])]
                                : entry.serves.filter((id) => id !== target.placeId)),
                          )
                        }
                      />{' '}
                      {target.name}
                    </label>
                  ))}
                </div>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.name} running`}
                    onCheckedChange={(on) => line(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  Running
                </label>
              </div>
            ))}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Stops">
            <h2 className="content-subtitle">Stops</h2>
            <p className="admin-muted">
              Places on the map the trains run to: the Standard fare, how long the ride takes, how far it is, and the
              stations passed on the way (one per line).
            </p>
            {rail.stops.map((item) => (
              <div className="content-grid content-row" key={item.placeId} data-testid={`stop-${item.placeId}`}>
                <Field id={`rstop-${item.placeId}-name`} label="Name">
                  <Input
                    id={`rstop-${item.placeId}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => stop(item.placeId, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`rstop-${item.placeId}-fare`} label="Standard fare (£)">
                  <Input
                    id={`rstop-${item.placeId}-fare`}
                    type="number"
                    min={0.5}
                    step="0.01"
                    value={item.farePence / 100}
                    onChange={(e) =>
                      stop(
                        item.placeId,
                        (entry) => void (entry.farePence = Math.max(50, toPence(e.target.value, item.farePence))),
                      )
                    }
                  />
                </Field>
                <Field id={`rstop-${item.placeId}-secs`} label="Ride (seconds)">
                  <Input
                    id={`rstop-${item.placeId}-secs`}
                    type="number"
                    min={10}
                    max={300}
                    value={item.seconds}
                    onChange={(e) =>
                      stop(
                        item.placeId,
                        (entry) =>
                          void (entry.seconds = within(Math.round(number(e.target.value, item.seconds)), 10, 300)),
                      )
                    }
                  />
                </Field>
                <Field id={`rstop-${item.placeId}-km`} label="Distance (km)">
                  <Input
                    id={`rstop-${item.placeId}-km`}
                    type="number"
                    min={1}
                    max={2000}
                    value={item.km}
                    onChange={(e) =>
                      stop(item.placeId, (entry) => void (entry.km = within(number(e.target.value, item.km), 1, 2000)))
                    }
                  />
                </Field>
                <Field id={`rstop-${item.placeId}-via`} label="Passing (one per line)">
                  <Textarea
                    id={`rstop-${item.placeId}-via`}
                    rows={3}
                    value={item.via.join('\n')}
                    onChange={(e) =>
                      stop(
                        item.placeId,
                        (entry) =>
                          void (entry.via = e.target.value
                            .split('\n')
                            .map((name) => name.trim().slice(0, 30))
                            .filter(Boolean)
                            .slice(0, 10)),
                      )
                    }
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`Trains to ${item.name}`}
                    onCheckedChange={(on) => stop(item.placeId, (entry) => void (entry.visible = on))}
                  />{' '}
                  Trains running
                </label>
              </div>
            ))}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Up the line">
            <h2 className="content-subtitle">Up the line</h2>
            <p className="admin-muted">The cities shown as opening soon, until they are built.</p>
            {rail.cities.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`city-${item.id}`}>
                <Field id={`city-${item.id}-name`} label="City">
                  <Input
                    id={`city-${item.id}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) =>
                      change((draft) => {
                        const entry = draft.cities.find((city) => city.id === item.id);
                        if (entry) entry.name = e.target.value;
                      })
                    }
                  />
                </Field>
                <Field id={`city-${item.id}-line`} label="Line">
                  <Input
                    id={`city-${item.id}-line`}
                    value={item.line}
                    maxLength={30}
                    onChange={(e) =>
                      change((draft) => {
                        const entry = draft.cities.find((city) => city.id === item.id);
                        if (entry) entry.line = e.target.value;
                      })
                    }
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.name} shown`}
                    onCheckedChange={(on) =>
                      change((draft) => {
                        const entry = draft.cities.find((city) => city.id === item.id);
                        if (entry) entry.visible = on;
                      })
                    }
                  />{' '}
                  Shown
                </label>
              </div>
            ))}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="The trip's card">
            <h2 className="content-subtitle">The trip's card</h2>
            <p className="admin-muted">In the arriving line, {'{stop}'} is where the train stops.</p>
            <div className="content-grid">
              {(['boarding', 'leaving', 'arriving'] as const).map((key) => (
                <Field key={key} id={`rail-${key}`} label={key[0]!.toUpperCase() + key.slice(1)}>
                  <Input
                    id={`rail-${key}`}
                    value={rail.script[key]}
                    maxLength={120}
                    onChange={(e) => change((draft) => void (draft.script[key] = e.target.value))}
                  />
                </Field>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
